import React, { useEffect } from "react";
import { IoPerson } from "@react-icons/all-files/io5/IoPerson";
import { FaPhoneAlt } from "@react-icons/all-files/fa/FaPhoneAlt";
import { GiHamburgerMenu } from "@react-icons/all-files/gi/GiHamburgerMenu";
import { CurvedArrowSvg } from "@Utils";
import { classNames } from "repoV2/utils/common/render/classNames";
import { BsCaretDownFill as DownIcon } from "@react-icons/all-files/bs/BsCaretDownFill";
import { BsCaretUpFill as UpIcon } from "@react-icons/all-files/bs/BsCaretUpFill";
import { PoweredByExly } from "repoV2/features/CreatorPages/modules/PoweredByExly";
import useMediaQuery from "@restart/hooks/cjs/useMediaQuery";
import { useScrollToSectionOnMount } from "@Utils/hooks/rendering";
import styles from "./navbarEduverse.module.scss";
import { INavbar } from "../../INavbar";
import { Notification } from "../../components/Notification";
import { Account } from "../../components/Account";
import { NAVBAR_ITEM_KEYS } from "../../data/getNavbarItems";
import { NavLink, LinkItem } from "../../components/NavLinks";

const NavbarEduverse = ({
    navbarItems,
    notificationProps,
    accountProps,
    hideMobHamMenu,
    onMobHamMenuClick,
    setIsMobHamMenuOpen,
    isMobHamMenuOpen,
    isOfferingsDropdownOpen,
    rootElement,
    className,
}: INavbar.IChildProps): JSX.Element => {
    // Automatically scroll to the section mentioned in url after "#" after 1200 ms.
    // why 1200 ms?? - to wait for page to load
    useScrollToSectionOnMount(1200);
    const isMobile: boolean = useMediaQuery("(max-width: 768px)");

    useEffect(() => {
        if (!isMobile) {
            setIsMobHamMenuOpen(false);
        }
    }, [isMobile]);

    const EventListingsDropdown = navbarItems.eventListings.showChildren ? (
        <div
            className={classNames(
                styles.eventListingsDropdownContainer,
                isOfferingsDropdownOpen && styles.open
            )}
        >
            {navbarItems.eventListings.children.map(navbarItem => (
                <a
                    key={navbarItem.content.toString()}
                    className={classNames(
                        styles.navbarItem,
                        styles.eventListingsDropdownItem,
                        isOfferingsDropdownOpen && styles.open
                    )}
                    {...navbarItem.props}
                >
                    {navbarItem.content}
                </a>
            ))}
        </div>
    ) : null;

    const showNavItems = [
        "team",
        "testimonials",
        "gallery",
        "blog",
    ] as Array<INavbar.INavbarItemKeys>;

    const DesktopMobCommonItems = () => (
        <>
            <NavLink
                showItems={showNavItems}
                navbarItems={navbarItems}
                classes={styles.navbarItem}
            />

            {navbarItems.externalLinks.map(item => (
                <LinkItem
                    // item.content is string for externalLinks
                    key={item.content.toString()}
                    content={item.content}
                    className={styles.navbarItem}
                    {...item.props}
                />
            ))}
        </>
    );

    const AccountAndNavigation = (
        <>
            <Notification
                {...notificationProps}
                notificationIconColorScheme={styles.colourScheme1}
            />
            <div className="position-relative">
                <Account
                    {...accountProps}
                    accountIconColorScheme={styles.colourScheme1}
                />
            </div>
        </>
    );

    return (
        <div className={classNames(styles.root, className)} ref={rootElement}>
            <a className={styles.navbarLogo} {...navbarItems.home.props}>
                {navbarItems.home.content}
            </a>
            <div className={classNames(styles.desktop, styles.rightContainer)}>
                <a className={styles.navbarItem} {...navbarItems.about.props}>
                    {navbarItems.about.content}
                </a>
                {navbarItems.eventListings.showItem ? (
                    <div
                        className={classNames(
                            styles.navbarItem,
                            styles.eventListingsButton,
                            navbarItems.eventListings.showChildren &&
                                styles.allowDropdown
                        )}
                        {...navbarItems.eventListings.props}
                    >
                        {navbarItems.eventListings.content}
                        {EventListingsDropdown}
                    </div>
                ) : null}

                <DesktopMobCommonItems />
                {AccountAndNavigation}
                {navbarItems.login.showItem ? (
                    <a
                        className={classNames(
                            styles.navbarItem,
                            styles.coloured,
                            styles.colourScheme1,
                            styles.hoverable,
                            styles.button
                        )}
                        {...navbarItems.login.props}
                    >
                        {navbarItems.login.content}
                    </a>
                ) : null}
                {navbarItems.getInTouch.showItem ? (
                    <div
                        className={classNames(
                            styles.navbarItem,
                            styles.coloured,
                            styles.colourScheme2,
                            styles.button
                        )}
                        {...navbarItems.getInTouch.props}
                    >
                        {navbarItems.getInTouch.content}
                    </div>
                ) : null}
            </div>

            <div className={classNames(styles.mobile, styles.rightContainer)}>
                {isMobHamMenuOpen ? null : AccountAndNavigation}
                {isMobile ? (
                    <>
                        {navbarItems.login.showItem ? (
                            <div
                                className={classNames(styles.button)}
                                {...navbarItems.login.props}
                            >
                                {isMobile ? (
                                    <IoPerson />
                                ) : (
                                    <>{navbarItems.login.content}</>
                                )}
                            </div>
                        ) : null}
                        {navbarItems.getInTouch.showItem ? (
                            <div
                                className={classNames(
                                    !isMobile && styles.iconButton,
                                    styles.button
                                )}
                                {...navbarItems.getInTouch.props}
                            >
                                {isMobile ? (
                                    <FaPhoneAlt />
                                ) : (
                                    navbarItems.getInTouch.content
                                )}
                            </div>
                        ) : null}
                        {!hideMobHamMenu ? (
                            <div
                                className={classNames(
                                    styles.button,
                                    isMobHamMenuOpen && styles.active
                                )}
                                onClick={onMobHamMenuClick}
                            >
                                <GiHamburgerMenu />
                            </div>
                        ) : null}
                    </>
                ) : (
                    <>
                        {!hideMobHamMenu ? (
                            <div
                                className={classNames(
                                    styles.mobHamMenuButton,
                                    styles.colourScheme2
                                )}
                                onClick={onMobHamMenuClick}
                            >
                                <span>
                                    {isMobHamMenuOpen ? "Close" : "Menu"}
                                </span>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
            <div
                className={classNames(
                    styles.mobile,
                    styles.mobileRoot,
                    isMobHamMenuOpen && styles.open
                )}
            >
                <a className={styles.navbarItem} {...navbarItems.about.props}>
                    {navbarItems.about.content}
                </a>
                {navbarItems.eventListings.showItem ? (
                    <div
                        className={classNames(styles.navbarItem)}
                        {...navbarItems.eventListings.props}
                    >
                        <div className={classNames(styles.eventListingsButton)}>
                            {navbarItems.eventListings.content}
                            {navbarItems.eventListings.showChildren ? (
                                <>
                                    &nbsp;&nbsp;
                                    {isOfferingsDropdownOpen ? (
                                        <UpIcon />
                                    ) : (
                                        <DownIcon />
                                    )}
                                </>
                            ) : null}
                        </div>
                        {EventListingsDropdown}
                    </div>
                ) : null}
                <DesktopMobCommonItems />
                <hr />
                {(
                    [
                        NAVBAR_ITEM_KEYS.myClasses,
                        NAVBAR_ITEM_KEYS.myMeetings,
                        NAVBAR_ITEM_KEYS.purchaseHistory,
                    ] as Array<INavbar.INavbarItemKeys>
                ).map(navbarItemKey => {
                    const navbarItem = navbarItems[navbarItemKey];
                    return navbarItem.showItem ? (
                        <a
                            key={navbarItemKey}
                            className={styles.navbarItem}
                            {...navbarItem.props}
                        >
                            {navbarItem.content}
                        </a>
                    ) : null;
                })}
                {navbarItems.getInTouch.showItem ? (
                    <div
                        className={classNames(
                            styles.colourScheme2,
                            styles.button
                        )}
                        {...navbarItems.getInTouch.props}
                    >
                        {navbarItems.getInTouch.content}
                    </div>
                ) : null}

                {(["login", "logout"] as Array<INavbar.INavbarItemKeys>).map(
                    navbarItemKey => {
                        const navbarItem = navbarItems[navbarItemKey];
                        return navbarItem.showItem ? (
                            <a
                                key={navbarItemKey}
                                className={classNames(
                                    styles.navButton,
                                    styles.button
                                )}
                                {...navbarItem.props}
                            >
                                {navbarItem.content}
                            </a>
                        ) : null;
                    }
                )}
                {/* 
                Replaced <div> with <section> to prevent styling issues during SSR.
                ---
                Reason:
                In SSR, if a <div> with styles is conditionally hidden, its styles might apply to the next sibling <div> due to hydration mismatches. Using <section> instead avoids this problem and improves HTML semantics.
                */}
                <section className={styles.eduverseArrowContainer}>
                    <div className={styles.eduverseArrow}>
                        <CurvedArrowSvg
                            className={styles.arrowIcon}
                            stroke="rgb(var(--rgbColor2-schema1-host-theme, 255, 255, 255))"
                        />
                    </div>
                </section>
                <hr />
                <section>
                    <PoweredByExly showCopyright />
                </section>
            </div>
        </div>
    );
};

export default NavbarEduverse;
